import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calendar, CheckCircle2, Clock, Info } from "lucide-react";
import { useMemo } from "react";

interface AgendaSheetProps {
  agendaSheetOpen: boolean;
  setAgendaSheetOpen: (open: boolean) => void;
  isAgendaLoading: boolean;
  agendaData: any[] | null | undefined;
  selectedAgendaParams: any;
}

export function AgendaSheet({
  agendaSheetOpen,
  setAgendaSheetOpen,
  isAgendaLoading,
  agendaData,
  selectedAgendaParams,
}: AgendaSheetProps) {
  const lecturerMeetingCounts = useMemo(() => {
    if (!agendaData) return [];
    const counts = agendaData.reduce((acc: any, curr: any) => {
      const name = curr.nama_lengkap || "Tidak Diketahui";
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count: count as number,
    }));
  }, [agendaData]);

  return (
    <Sheet open={agendaSheetOpen} onOpenChange={setAgendaSheetOpen}>
      <SheetContent className="max-w-500! w-[90vw]! flex flex-col p-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto w-full p-0">
          <SheetHeader className="mb-4 px-4 sm:px-6 pt-6">
            <SheetTitle className="text-xl flex items-center gap-2.5 text-slate-800 font-bold">
              <div className="p-2 bg-secondary/80 text-primary rounded-lg">
                <Calendar className="w-5 h-5" />
              </div>
              Daftar Agenda
            </SheetTitle>
            <SheetDescription className="flex flex-col gap-3 mt-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant="secondary"
                  className="bg-indigo-50 text-indigo-700 border border-indigo-200 font-semibold text-[10px] px-2.5 py-0.5 shadow-sm"
                >
                  Diambil dari SIAKNG
                </Badge>
                {/* <Badge
                  variant="secondary"
                  className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold text-[10px] px-2.5 py-0.5 shadow-sm"
                >
                  Diambil dari matakuliah biasa
                </Badge> */}
              </div>
              {lecturerMeetingCounts.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {lecturerMeetingCounts.map((lecturer, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="bg-amber-50 text-amber-700 border-amber-200 font-medium text-[10px] px-2.5 py-1"
                    >
                      {lecturer.name}:{" "}
                      <span className="font-bold ml-1">
                        {lecturer.count} Pertemuan
                      </span>
                    </Badge>
                  ))}
                </div>
              )}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-6 px-4 pb-8 sm:px-6">
            <div className="rounded-xl border border-slate-200 bg-white shadow-md **:data-[slot=table-container]:overflow-visible">
              <Table>
                <TableHeader className="bg-slate-100/95 backdrop-blur-sm border-b-2 border-slate-200 sticky top-0 z-20 shadow-sm outline outline-slate-200">
                  <TableRow className="hover:bg-transparent border-none">
                    <TableHead
                      rowSpan={2}
                      className="w-16 text-center align-middle font-bold text-slate-700 border-r border-slate-200 uppercase text-[9px] tracking-wider"
                    >
                      Pertemuan
                    </TableHead>
                    <TableHead
                      rowSpan={2}
                      className="w-28 align-middle font-bold text-slate-700 border-r border-slate-200 uppercase text-[9px] tracking-wider"
                    >
                      Tanggal
                    </TableHead>
                    <TableHead
                      rowSpan={2}
                      className="w-62.5 max-w-62.5 align-middle font-bold text-slate-700 border-r border-slate-200 uppercase text-[9px] tracking-wider"
                    >
                      Uraian Materi
                    </TableHead>
                    <TableHead
                      rowSpan={2}
                      className="w-50 max-w-50 align-middle font-bold text-slate-700 border-r border-slate-200 uppercase text-[9px] tracking-wider"
                    >
                      Dosen
                    </TableHead>
                    <TableHead
                      colSpan={4}
                      className="text-center align-middle font-bold text-slate-700 border-r border-b border-slate-200 uppercase text-[9px] tracking-wider py-2"
                    >
                      Kehadiran Mahasiswa
                    </TableHead>
                    <TableHead
                      rowSpan={2}
                      className="min-w-28 align-middle font-bold text-slate-700 border-r border-slate-200 uppercase text-[9px] tracking-wider"
                    >
                      Validasi
                      <br />
                      Korti
                    </TableHead>
                    <TableHead
                      rowSpan={2}
                      className="min-w-28 align-middle font-bold text-slate-700 border-r border-slate-200 uppercase text-[9px] tracking-wider"
                    >
                      Validasi
                      <br />
                      Kaprodi
                    </TableHead>
                    <TableHead
                      rowSpan={2}
                      className="min-w-28 align-middle font-bold text-slate-700 border-r border-slate-200 uppercase text-[9px] tracking-wider"
                    >
                      Validasi
                      <br />
                      Elearning
                    </TableHead>
                    <TableHead
                      rowSpan={2}
                      className="min-w-36 align-middle font-bold text-slate-700 uppercase text-[9px] tracking-wider"
                    >
                      Status
                    </TableHead>
                  </TableRow>
                  <TableRow className="hover:bg-transparent bg-slate-50/50 border-none">
                    <TableHead className="w-16 text-center font-bold text-slate-600 border-r border-slate-200 h-9 uppercase text-[9px]">
                      Hadir
                    </TableHead>
                    <TableHead className="w-16 text-center font-bold text-slate-600 border-r border-slate-200 h-9 uppercase text-[9px]">
                      Ijin
                    </TableHead>
                    <TableHead className="w-16 text-center font-bold text-slate-600 border-r border-slate-200 h-9 uppercase text-[9px]">
                      Sakit
                    </TableHead>
                    <TableHead className="w-16 text-center font-bold text-slate-600 border-r border-slate-200 h-9 uppercase text-[9px]">
                      Alpha
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isAgendaLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={12}
                        className="h-48 text-center text-slate-500"
                      >
                        <div className="flex flex-col items-center justify-center gap-2">
                          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <p className="text-xs">Memuat data agenda...</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : agendaData?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={12}
                        className="h-48 text-center text-slate-500"
                      >
                        <p className="text-xs">
                          Tidak ada data agenda ditemukan.
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    agendaData?.map((agenda, i) => {
                      const isKortiValid = agenda.status_agenda === 1;
                      const isKaprodiValid = agenda.status_kaprodi === 1;
                      const isElearningValid =
                        agenda.status_validasi_elearning === 1;
                      const isAllValid =
                        isKortiValid && isKaprodiValid && isElearningValid;
                      const rowStatus = isAllValid
                        ? "Tervalidasi"
                        : "Menunggu Validasi";

                      return (
                        <TableRow
                          key={i}
                          className={`border-b transition-colors ${isAllValid ? "hover:bg-slate-50 border-slate-100" : "bg-rose-50/70 hover:bg-rose-100/50 border-rose-200"}`}
                        >
                          <TableCell
                            className={`text-center border-r font-semibold text-slate-600 ${isAllValid ? "bg-slate-50/30 border-slate-100" : "bg-rose-100/50 border-rose-200"}`}
                          >
                            {agenda.pertemuan_ke}
                          </TableCell>
                          <TableCell
                            className={`border-r tabular-nums text-slate-600 text-[11px] font-medium ${isAllValid ? "border-slate-100" : "border-rose-200"}`}
                          >
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-slate-400" />
                              {agenda.tanggal}
                            </div>
                          </TableCell>
                          <TableCell
                            className={`border-r font-medium text-slate-800 text-[11px] leading-snug w-62.5 max-w-62.5 whitespace-normal wrap-break-word ${isAllValid ? "border-slate-100" : "border-rose-200"}`}
                          >
                            {agenda.uraian_materi}
                          </TableCell>
                          <TableCell
                            className={`border-r text-xs text-slate-700 w-100 max-w-100 whitespace-normal wrap-break-word ${isAllValid ? "border-slate-100" : "border-rose-200"}`}
                          >
                            <div className="flex items-start gap-2">
                              <div
                                className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[9px] font-bold ${isAllValid ? "bg-secondary/80 text-primary" : "bg-rose-200 text-rose-800"}`}
                              >
                                {agenda.nama_lengkap.charAt(0)}
                              </div>
                              <span>{agenda.nama_lengkap}</span>
                            </div>
                          </TableCell>
                          <TableCell
                            className={`text-center border-r tabular-nums font-medium text-[11px] text-slate-700 ${isAllValid ? "bg-emerald-50/20 border-slate-100" : "bg-transparent border-rose-200"}`}
                          >
                            {agenda.hadir ?? "-"}
                          </TableCell>
                          <TableCell
                            className={`text-center border-r tabular-nums font-medium text-[11px] text-slate-700 ${isAllValid ? "bg-amber-50/20 border-slate-100" : "bg-transparent border-rose-200"}`}
                          >
                            {agenda.ijin ?? "-"}
                          </TableCell>
                          <TableCell
                            className={`text-center border-r tabular-nums font-medium text-[11px] text-slate-700 ${isAllValid ? "bg-orange-50/20 border-slate-100" : "bg-transparent border-rose-200"}`}
                          >
                            {agenda.sakit ?? "-"}
                          </TableCell>
                          <TableCell
                            className={`text-center border-r tabular-nums font-medium text-[11px] text-slate-700 ${isAllValid ? "bg-red-50/20 border-slate-100" : "bg-transparent border-rose-200"}`}
                          >
                            {agenda.alpha ?? "-"}
                          </TableCell>
                          <TableCell
                            className={`border-r ${isAllValid ? "border-slate-100" : "border-rose-200"}`}
                          >
                            <div
                              className={`flex items-center gap-1.5 font-medium text-[10px] px-2 py-1 rounded-md w-fit border ${isKortiValid ? "text-emerald-600 bg-emerald-50 border-emerald-100" : "text-rose-600 bg-rose-100 border-rose-200"}`}
                            >
                              {isKortiValid ? (
                                <CheckCircle2 className="w-3 h-3" />
                              ) : (
                                <Clock className="w-3 h-3" />
                              )}{" "}
                              {isKortiValid ? "Sudah" : "Belum"}
                            </div>
                          </TableCell>
                          <TableCell
                            className={`border-r ${isAllValid ? "border-slate-100" : "border-rose-200"}`}
                          >
                            <div
                              className={`flex items-center gap-1.5 font-medium text-[10px] px-2 py-1 rounded-md w-fit border ${isKaprodiValid ? "text-emerald-600 bg-emerald-50 border-emerald-100" : "text-rose-600 bg-rose-100 border-rose-200"}`}
                            >
                              {isKaprodiValid ? (
                                <CheckCircle2 className="w-3 h-3" />
                              ) : (
                                <Clock className="w-3 h-3" />
                              )}{" "}
                              {isKaprodiValid ? "Sudah" : "Belum"}
                            </div>
                          </TableCell>
                          <TableCell
                            className={`border-r ${isAllValid ? "border-slate-100" : "border-rose-200"}`}
                          >
                            <div
                              className={`flex items-center gap-1.5 font-medium text-[10px] px-2 py-1 rounded-md w-fit border ${isElearningValid ? "text-emerald-600 bg-emerald-50 border-emerald-100" : "text-rose-600 bg-rose-100 border-rose-200"}`}
                            >
                              {isElearningValid ? (
                                <CheckCircle2 className="w-3 h-3" />
                              ) : (
                                <Clock className="w-3 h-3" />
                              )}{" "}
                              {isElearningValid ? "Sudah" : "Belum"}
                            </div>
                          </TableCell>
                          <TableCell className="">
                            <Badge
                              className={`border-none text-[10px] font-semibold shadow-sm w-full justify-center ${isAllValid ? "bg-sky-100 text-sky-700 hover:bg-sky-200" : "bg-rose-100 text-rose-700 hover:bg-rose-200"}`}
                            >
                              {rowStatus}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                <Info className="w-16 h-16" />
              </div>
              <h4 className="text-[11px] font-semibold uppercase text-slate-500 mb-3 tracking-wider">
                Parameter Fetching Terpilih
              </h4>
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-y-3 gap-x-4 text-[11px] relative z-10">
                <div className="text-slate-500">NIP Dosen</div>
                <div className="font-medium col-span-1 lg:col-span-2 text-slate-800">
                  {selectedAgendaParams?.nip || "-"}
                </div>
                <div className="text-slate-500">Kode Matkul</div>
                <div className="font-medium col-span-1 lg:col-span-2 text-slate-800">
                  {selectedAgendaParams?.kode_matkul || "-"}
                </div>
                <div className="text-slate-500">Tahun / Semester</div>
                <div className="font-medium col-span-1 lg:col-span-2 text-slate-800">
                  {selectedAgendaParams?.tahun} /{" "}
                  {selectedAgendaParams?.semester}
                </div>
                <div className="text-slate-500">Kelas</div>
                <div className="font-medium col-span-1 lg:col-span-2 text-slate-800">
                  {selectedAgendaParams?.kelas}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
